import axios from "axios";

const igAxios = axios.create({
  baseURL: process.env.REACT_APP_IG_ADAPTER_URL,
  headers: {
    "X-Api-Key": process.env.REACT_APP_IG_ADAPTER_API_KEY,
    "Content-Type": "application/json",
  },
});

// Sertakan X-User-ID dan X-User-Role dari localStorage di setiap request ke ig-adapter
igAxios.interceptors.request.use((config) => {
  try {
    const userDetail = JSON.parse(localStorage.getItem("userDetail") || "{}");
    if (userDetail?.id) {
      config.headers["X-User-ID"] = userDetail.id;
    }
    const roleName = userDetail?.role?.name;
    if (roleName) {
      config.headers["X-User-Role"] = roleName;
    }
  } catch (e) {
    // localStorage parse error — lanjut tanpa header
    console.log(e);
  }
  return config;
});

// GET profile dari ig-connect — avatar URL dan detail user Instagram
// Response: { id, username, name, avatarUrl, followerCount, ... }
export const igGetProfile = async (accountSlug, participantUserId) => {
  try {
    const baseUrl = process.env.REACT_APP_IG_CONNECT_URL || "https://ig-connect.astronacci.co.id";
    const response = await axios.get(`${baseUrl}/instagram/${accountSlug}/profile/${participantUserId}`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// GET /accounts — list semua akun IG yang tersedia
export const igGetAccounts = async () => {
  try {
    const baseUrl = process.env.REACT_APP_IG_CONNECT_URL || "https://ig-connect.astronacci.co.id";
    const response = await axios.get(`${baseUrl}/accounts`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// GET /chats?[accounts=slug1,slug2|department_id=uuid]&limit=N&offset=N[&search=q]
// Jika departmentId ada → tidak perlu accounts param (backend filter by dept).
// Jika tidak ada departmentId → wajib pass accountSlugs (fallback lama).
export const igGetChats = async (accountSlugs, limit = 20, offset = 0, search = "", departmentId = "") => {
  try {
    let url = `/chats?limit=${limit}&offset=${offset}`;
    if (departmentId) {
      // departmentId bisa berupa satu UUID atau beberapa UUID dipisah koma
      url += `&department_id=${departmentId}`;
    } else {
      const accounts = Array.isArray(accountSlugs) ? accountSlugs.join(",") : accountSlugs;
      if (accounts) url += `&accounts=${accounts}`;
    }
    if (search) url += `&search=${encodeURIComponent(search)}`;
    const response = await igAxios.get(url);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// GET /chats/:id
export const igGetChatById = async (chatId) => {
  try {
    const response = await igAxios.get(`/chats/${chatId}`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// GET /chats/:id/messages?limit=N&offset=N
export const igGetMessages = async (chatId, limit = 50, offset = 0) => {
  try {
    const response = await igAxios.get(`/chats/${chatId}/messages?limit=${limit}&offset=${offset}`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// POST /chats/:id/messages — kirim pesan ke pelanggan.
// replyToId: optional message_id being replied to (CRM-side quote only —
// Instagram's API cannot deliver quoted replies to the customer).
export const igSendMessage = async (chatId, text, replyToId = "") => {
  try {
    const response = await igAxios.post(`/chats/${chatId}/messages`, {
      message: text,
      ...(replyToId ? { reply_to_id: replyToId } : {}),
    });
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// PUT /chats/:id/assign — assign conversation ke agent
// actorName: nama user yang melakukan assign (logged-in user)
// previousName: nama assignee sebelumnya (kosong jika belum ada)
// targetName: nama agent yang di-assign
export const igAssignAgent = async (chatId, userId, actorName = "", previousName = "", targetName = "") => {
  try {
    const response = await igAxios.put(`/chats/${chatId}/assign`, {
      assigned_user_id: userId,
      actor_name: actorName,
      previous_name: previousName,
      target_name: targetName,
    });
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// DELETE /chats/:id/assign — unassign conversation dari agent
// actorName: nama user yang melakukan unassign, previousName: nama assignee yang di-unassign
export const igUnassignAgent = async (chatId, actorName = "", previousName = "") => {
  try {
    const response = await igAxios.delete(`/chats/${chatId}/assign`, {
      data: { actor_name: actorName, previous_name: previousName },
    });
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// GET /chats/:id/notifications — ambil notifikasi sistem per conversation
export const igGetNotifications = async (chatId, limit = 100, offset = 0) => {
  try {
    const response = await igAxios.get(`/chats/${chatId}/notifications?limit=${limit}&offset=${offset}`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// PUT /chats/:id/read — tandai conversation sudah dibaca (reset unread_count)
export const igMarkChatRead = async (chatId) => {
  try {
    const response = await igAxios.put(`/chats/${chatId}/read`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// GET /accounts/:slug/greeting — ambil config greeting untuk akun
export const igGetGreetingConfig = async (slug) => {
  try {
    const response = await igAxios.get(`/accounts/${slug}/greeting`);
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// PUT /accounts/:slug/greeting — update config greeting untuk akun
export const igUpdateGreetingConfig = async (slug, enabled, message, cooldownHours = 0) => {
  try {
    const response = await igAxios.put(`/accounts/${slug}/greeting`, {
      enabled,
      message,
      cooldown_hours: cooldownHours,
    });
    return response;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};

// igSendMediaMessage — single step: upload S3 + kirim ke Instagram + simpan DB dalam satu request
export const igSendMediaMessage = async (chatId, file, type, message = "") => {
  try {
    const category = type === "image" ? "ig-image" : type === "video" ? "ig-video" : "ig-document";
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", category);
    formData.append("chat_id", chatId);
    if (message) formData.append("caption", message);

    const res = await igAxios.post("upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    if (res?.data?.url) res.data._attachmentUrl = res.data.url;
    return res;
  } catch (error) {
    return error.response || { status: "No Server Response" };
  }
};
